import { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Select, MenuItem, styled, useMediaQuery, useTheme } from '@mui/material';
import { StyledOutlinedButton } from '../detailPageLayout/HeaderSection';
import { buildImageUrl } from '../../utils/utils';
import { useTranslation } from 'next-i18next';
import FloorPlanGallery from './FloorPlanGallery';

export const StyledScroll = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#a0a0a0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  borderRadius: 50,
  minWidth: 127,
  padding: '8px 16px',
  border: '0.5px solid #E1E1E2',
  transition: 'all 0.3s ease',
  textTransform: 'none',
  color: theme.palette.common.black,
  '& .icon': {
    color: theme.palette.primary.main,
    transition: 'color 0.3s',
  },
  '&:hover, &.Mui-selected': {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    borderColor: 'transparent',
    '& .icon': {
      color: theme.palette.common.white,
    },
  },
}));

const StyledTabs = ({ t, value, onChange, floorPlans, units }) => (
  <Tabs
    value={value}
    onChange={onChange}
    TabIndicatorProps={{ style: { display: 'none' } }}
    sx={{
      borderRadius: '999px',
      backgroundColor: 'transparent',
      width: 'fit-content',
      mb: 3,
      mx: 'auto',
      gap: 2,
    }}
  >
    <StyledTab value="project" label={t('project-plan')} disabled={floorPlans?.length <= 0} sx={{ mr: 2 }} />
    <StyledTab value="unit" label={t('room-style')} disabled={units?.length <= 0} />
  </Tabs>
);

// ---------------- Panels ----------------

const ProjectPanel = ({ t, building, setBuilding, floorList, floor, setFloor, buildingNames }) => (
  <Box>
    <Typography fontWeight={600} mb={1}>
      {t('building-unit')}
    </Typography>
    <Select
      value={building}
      onChange={(e) => setBuilding(e.target.value)}
      fullWidth
      size="small"
      sx={{ mb: 3, borderRadius: 3 }}
    >
      {buildingNames.map((b) => (
        <MenuItem key={b} value={b}>
          {b}
        </MenuItem>
      ))}
    </Select>

    <Typography fontWeight={600} mb={1}>
      {t('floor-unit')}
    </Typography>
    <StyledScroll
      sx={{
        maxHeight: 290,
        pr: 1,
      }}
    >
      {floorList.map((f) => (
        <StyledOutlinedButton
          key={f.id}
          onClick={() => setFloor(f.label)}
          fullWidth
          sx={{
            mb: 1,
            backgroundColor: f.label === floor ? 'primary.main' : 'common.white',
            color: f.label === floor ? 'common.white' : 'common.black',
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              width: '100%',
              textAlign: 'left',
            }}
          >
            {f.label}
          </span>
        </StyledOutlinedButton>
      ))}
    </StyledScroll>
  </Box>
);

const UnitPanel = ({ t, selectUnit, setSelectUnit, units }) => (
  <Box>
    <Typography fontWeight={600} mb={1}>
      {t('room-style')}
    </Typography>

    <StyledScroll
      sx={{
        maxHeight: 385,
        pr: 1,
      }}
    >
      {units?.map((unit) => (
        <StyledOutlinedButton
          key={unit.id}
          onClick={() => setSelectUnit(unit.id)}
          fullWidth
          sx={{
            mb: 1,
            backgroundColor: unit.id === selectUnit ? 'primary.main' : 'common.white',
            color: unit.id === selectUnit ? 'common.white' : 'common.black',
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              width: '100%',
              textAlign: 'left',
            }}
          >
            {`${unit.type} ${unit.series} ${unit.size} ${unit.unit}`}
          </span>
        </StyledOutlinedButton>
      ))}
    </StyledScroll>
  </Box>
);

// ---------------- Main Section ----------------

const ProjectFloorPlanSection = ({ projectId, floorPlans, units }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [view, setView] = useState<'project' | 'unit' | ''>(
    floorPlans.length > 0 ? 'project' : units.length > 0 ? 'unit' : '',
  );
  const [building, setBuilding] = useState('');
  const [floor, setFloor] = useState('');
  const [selectUnit, setSelectUnit] = useState(units?.[0]?.id ?? null);

  const buildingNames = useMemo(() => floorPlans?.map((b) => b.name) ?? [], [floorPlans]);

  const floorList = useMemo(() => {
    return (
      floorPlans
        ?.find((b) => b.name === building)
        ?.floorPlans?.map((f) => ({
          id: f.floor_ID,
          label: f.floor_name,
          image: f.image,
          floor_start: f.floor_start,
        })) ?? []
    );
  }, [floorPlans, building]);

  const selectedFloor = useMemo(() => {
    return floorList.find((f) => f.label === floor);
  }, [floorList, floor]);

  useEffect(() => {
    if (floorPlans?.length && !building) {
      const firstBuilding = floorPlans[0];
      setBuilding(firstBuilding.name);
    }
  }, [floorPlans, building]);

  useEffect(() => {
    const currentBuilding = floorPlans?.find((b) => b.name === building);
    const floors = currentBuilding?.floorPlans ?? [];

    if (floors.length > 0) {
      setFloor(floors[0].floor_name);
    } else {
      setFloor('');
    }
  }, [building, floorPlans]);

  useEffect(() => {
    if (view === 'project') {
      const currentBuilding = floorPlans?.find((b) => b.name === building);
      const floors = currentBuilding?.floorPlans ?? [];
      const floorOne = floors.find((f) => f.floor_start === 1);
      setFloor(floorOne?.floor_name || floors[0]?.floor_name || '');
    } else if (view === 'unit') {
      setSelectUnit(units?.[0]?.id ?? null);
    }
  }, [view]);

  const getSlides = () => {
    if (view === 'project') {
      if (!selectedFloor?.image) return [];

      const pathInfo = {
        galleryPath: 'project_floorplan',
        pathId: projectId ?? '',
        fileName: selectedFloor.image,
      };

      return [
        {
          src: buildImageUrl({
            ...pathInfo,
            isDesktop,
            quality: 60,
          }),
          alt: selectedFloor.label || '',
        },
      ];
    }

    if (view === 'unit') {
      const selectedUnit = units.find((u) => u.id === selectUnit);
      if (!selectedUnit) return [];

      const unitImages: string[] = [];

      if (Array.isArray(selectedUnit.images)) {
        unitImages.push(...selectedUnit.images.map((img) => img.image));
      }

      const oldUnitImages = [selectedUnit.unit_Images1, selectedUnit.unit_Images2, selectedUnit.unit_Images3].filter(
        Boolean,
      );
      unitImages.push(...oldUnitImages);

      return unitImages.map((img) => {
        const pathInfo = {
          galleryPath: 'project_unit',
          pathId: projectId ?? '',
          fileName: img,
        };

        return {
          src: buildImageUrl({
            ...pathInfo,
            isDesktop,
            quality: 60,
          }),
          thumbnail: buildImageUrl({
            ...pathInfo,
            width: 150,
            height: 150,
            quality: 20,
          }),
          alt: selectedUnit.type || '',
        };
      });
    }

    return [];
  };

  const slides = useMemo(() => getSlides(), [view, selectedFloor, selectUnit]);

  return (
    <Box>
      <StyledTabs t={t} value={view} onChange={(_, val) => setView(val)} floorPlans={floorPlans} units={units} />

      <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
        <Box width={{ xs: '100%', md: 275 }}>
          {view === 'project' ? (
            <ProjectPanel
              t={t}
              building={building}
              setBuilding={setBuilding}
              floor={floor}
              setFloor={setFloor}
              floorList={floorList}
              buildingNames={buildingNames}
            />
          ) : (
            <UnitPanel t={t} selectUnit={selectUnit} setSelectUnit={setSelectUnit} units={units} />
          )}
        </Box>
        <Box flex={1}>
          <FloorPlanGallery slides={slides} />
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectFloorPlanSection;
